import { getKeyFromDataFieldWithDataType } from '../../../common-types';
import { Language } from '../graphql/generated/graphql-sdk';
import { Hasher } from '../v2/api/hasher';
import { HasDependencies, hashString, RoleId } from '../v2/api/types';
import { handleGQLErrors } from '../v2/api/utils';
import { Domains, Languages } from '../v2/constants';
import {
  AliasTypes,
  NewApiAlias,
  NewApiAliasFor,
  NewApiAliasForDataField,
  NewApiAliasForMetricCategoryId,
  NewApiAliasForMetricGroupId,
  NewApiAliasTranslation,
} from './api-interfaces';
import { GraphQlRequestService } from './graphql-request-service';

export interface IAliasService extends HasDependencies {
  setAliasForDataField(alias: NewApiAliasForDataField): Promise<void>;
  setAliasForMetricGroupId(alias: NewApiAliasForMetricGroupId): Promise<void>;
  setAliasForMetricCategoryId(
    alias: NewApiAliasForMetricCategoryId
  ): Promise<void>;
  deleteAliasForDataField(alias: NewApiAliasForDataField): Promise<void>;
  deleteAliasForMetricGroupId(
    alias: NewApiAliasForMetricGroupId
  ): Promise<void>;
  deleteAliasForMetricCategoryId(
    alias: NewApiAliasForMetricCategoryId
  ): Promise<void>;
}

export class AliasService implements IAliasService {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly aliases: NewApiAlias[],
    readonly simulateRole: RoleId | null,
    readonly domain: Domains
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      Hasher.hash(this.aliases),
      Hasher.hash(this.simulateRole),
      this.domain,
    ]),
  ];

  private convertTranslations = (translations: NewApiAliasTranslation[]) =>
    translations.map((translation) => {
      let apiLang: Language;
      const language = translation.language as string;
      if (language === Languages.EN) {
        apiLang = Language.En;
      } else if (language === Languages.JA) {
        apiLang = Language.Jp;
      } else if (language === 'jp') {
        // Only aliases use 'jp' as the language
        apiLang = Language.Jp;
      } else {
        throw new Error(
          `Unexpected alias language ${language}. Cannot convert it into API language. Expected en or ja.`
        );
      }

      return {
        text: translation.text,
        language: apiLang,
      };
    });

  public async setAliasForDataField(
    alias: NewApiAliasForDataField
  ): Promise<void> {
    return this.setAlias(
      alias,
      getKeyFromDataFieldWithDataType(alias.dataFieldWithDataType),
      AliasTypes.DATAFIELD
    );
  }

  public async setAliasForMetricGroupId(
    alias: NewApiAliasForMetricGroupId
  ): Promise<void> {
    return this.setAlias(
      alias,
      alias.metricGroupId,
      AliasTypes.METRIC_GROUP_ID
    );
  }

  public async setAliasForMetricCategoryId(
    alias: NewApiAliasForMetricCategoryId
  ): Promise<void> {
    return this.setAlias(
      alias,
      alias.metricCategoryId,
      AliasTypes.METRIC_CATEGORY_ID
    );
  }

  private async setAlias(
    alias: NewApiAliasFor,
    key: string,
    type: AliasTypes
  ): Promise<void> {
    const convertedTranslations = this.convertTranslations(alias.translations);

    await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.setAlias({
        domain: this.domain,
        key,
        type,
        translations: convertedTranslations,
        simulateRole: this.simulateRole,
      }),
      {
        request: 'setAlias',
        domain: this.domain,
        key,
        type,
      }
    );

    return;
  }

  public async deleteAliasForDataField(
    alias: NewApiAliasForDataField
  ): Promise<void> {
    return this.deleteAlias(() =>
      getKeyFromDataFieldWithDataType(alias.dataFieldWithDataType)
    );
  }

  public async deleteAliasForMetricGroupId(
    alias: NewApiAliasForMetricGroupId
  ): Promise<void> {
    return this.deleteAlias(() => alias.metricGroupId);
  }

  public async deleteAliasForMetricCategoryId(
    alias: NewApiAliasForMetricCategoryId
  ): Promise<void> {
    return this.deleteAlias(() => alias.metricCategoryId);
  }

  private async deleteAlias(getKey: () => string): Promise<void> {
    await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.deleteAlias({
        domain: this.domain,
        aliasKey: getKey(),
        simulateRole: this.simulateRole,
      }),
      {
        request: 'deleteAlias',
        domain: this.domain,
        aliasKey: getKey(),
      }
    );

    return;
  }
}
